import React, { useEffect, useState, useCallback } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { GlobalEvents } from "@bryntum/schedulerpro";
import { SWIVU_ASSIST_COLUMN_MAP } from "@/other/constants/OtherKeys";
import { Card, CardContent } from "@/modern/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";

export const SWIVU_ASSIST_UPDATE_EVENT = "swivuAssistUpdate";

export function triggerSwivuAssistUpdate() {
  GlobalEvents.trigger(SWIVU_ASSIST_UPDATE_EVENT);
}

interface SwivuAssistanceConfig {
  type: string;
  popupTitle: string;
}

interface SwivuAssistanceContent {
  box1?: string;
  box2?: string;
  box3?: string;
}

interface SwivuAssistEditorProps {
  record: {
    swivuAssistanceConfig: SwivuAssistanceConfig;
    [key: string]: any;
  };
  onClose: () => void;
  editorInstance?: any;
  context?: any;
}

export const SwivuAssistEditor = ({ record, onClose, editorInstance }: SwivuAssistEditorProps) => {
  const { swivuAssistanceConfig } = record;
  const [assistanceContent, setAssistanceContent] = useState<SwivuAssistanceContent>({});

  const loadAssistanceContent = useCallback(() => {
    if (!swivuAssistanceConfig) {
      console.error("Please provide a swivuAssistanceConfig to editor");
      return;
    }

    const { type } = swivuAssistanceConfig;
    const content = (window as any).swivuAssistanceRecord?.[type] || {};
    setAssistanceContent(content);
  }, [swivuAssistanceConfig]);

  useEffect(() => {
    loadAssistanceContent();

    const listener = GlobalEvents.on({
      [SWIVU_ASSIST_UPDATE_EVENT]: loadAssistanceContent
    });

    return () => {
      listener();
    };
  }, [loadAssistanceContent]);

  const { box1, box2, box3 } = assistanceContent;
  const { popupTitle } = swivuAssistanceConfig;

  // Find the assist key for the tooltip
  const swivuAssistKey = Object.keys(SWIVU_ASSIST_COLUMN_MAP).find((key) => SWIVU_ASSIST_COLUMN_MAP[key as keyof typeof SWIVU_ASSIST_COLUMN_MAP] === swivuAssistanceConfig.type);

  const tooltipText = swivuAssistKey ? `Swivu Assist (ID: ${swivuAssistKey})` : "Swivu Assist";

  const renderColumn = (content: string | undefined) => (
    <Card className="h-full bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6 h-full overflow-auto">
        <div
          className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:leading-relaxed [&_p]:mb-4 [&_strong]:text-foreground [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: content || '<p class="text-muted-foreground italic">Content coming soon...</p>' }}
        />
      </CardContent>
    </Card>
  );

  const headerRenderer = () => (
    <div className="drag-handle flex items-center justify-between p-4 border-b border-border">
      <div className="flex items-center gap-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center size-10 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors cursor-help">
                <i className="fa-regular fa-circle-question text-xl text-primary" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-sm">{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <h2 className="text-xl font-semibold capitalize m-0 truncate max-w-[40rem]">{popupTitle}</h2>
      </div>

      <button
        onClick={onClose}
        className="size-9 hover:bg-destructive/10 hover:text-destructive rounded-lg transition-all duration-200 bg-transparent border-0 cursor-pointer flex items-center justify-center group"
        aria-label="Close"
      >
        <i className="text-muted-foreground group-hover:text-destructive fas fa-times text-lg transition-colors"></i>
      </button>
    </div>
  );

  return (
    <BaseModal
      record={{ isPhantom: true, archived: false }}
      onClose={onClose}
      title={popupTitle}
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "71.5rem",
        height: "32.5rem",
        minWidth: "50rem",
        minHeight: "32rem",
        maxHeight: "100vh",
        maxWidth: "100vw"
      }}
      modal={true}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      headerRenderer={headerRenderer}
    >
      <BaseModalBody>
        <div className="grid grid-cols-3 gap-4 h-full">
          {renderColumn(box1)}
          {renderColumn(box2)}
          {renderColumn(box3)}
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};
